import type { AccountInfo } from '@azure/msal-browser'
import axios, { type AxiosError, CanceledError, type InternalAxiosRequestConfig } from 'axios'

import type { ProblemDetails, ProblemDetailsError } from '../data/types/ProblemDetails'
import { showProblemDetailsToast, showToast } from '../utils/fluentToast'

import { acquireAccessToken, msalInstance } from './msalInstanceHelper'

// Extend axios config types
declare module 'axios' {
  interface AxiosRequestConfig {
    skipNotFound?: boolean
    authAccount?: AccountInfo
  }
}

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

const isProblemDetailsResponse = (data: unknown): data is ProblemDetails => {
  return (
    data !== null &&
    typeof data === 'object' &&
    'type' in data &&
    'title' in data &&
    'status' in data &&
    'detail' in data &&
    typeof (data as Record<string, unknown>).type === 'string' &&
    typeof (data as Record<string, unknown>).title === 'string' &&
    typeof (data as Record<string, unknown>).status === 'number' &&
    typeof (data as Record<string, unknown>).detail === 'string'
  )
}

const createProblemDetailsError = (axiosError: AxiosError): ProblemDetailsError => {
  const problemDetails = axiosError.response?.data as ProblemDetails
  const error = new Error(problemDetails.title || 'An error occurred') as ProblemDetailsError
  error.problemDetails = problemDetails
  error.response = axiosError.response as ProblemDetailsError['response']
  return error
}

// ── Axios SINGLETON ──────────────────────────────────────────────────────
const apiClient = axios.create({
  baseURL: String(import.meta.env.VITE_API_BASE_PATH),
  timeout: 450000,
  headers: { 'Content-Type': 'application/json' },
})

const ensureCurrentAccount = (config: InternalAxiosRequestConfig) => {
  if (config.signal?.aborted) {
    throw new CanceledError()
  }

  if (!config.authAccount) {
    return
  }

  const accounts = msalInstance.getAllAccounts()
  const account = msalInstance.getActiveAccount() ?? (accounts.length > 0 ? accounts[0] : null)
  if (
    !account ||
    account.homeAccountId !== config.authAccount.homeAccountId ||
    account.localAccountId !== config.authAccount.localAccountId ||
    account.tenantId !== config.authAccount.tenantId
  ) {
    throw new CanceledError('The active account changed')
  }
}

// Request interceptor — registered ONCE
apiClient.interceptors.request.use(async (config) => {
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }

  ensureCurrentAccount(config)
  const accessToken = await acquireAccessToken(config.authAccount)
  ensureCurrentAccount(config)
  config.headers.Authorization = `Bearer ${accessToken}`
  return config
})

// Response interceptor — registered ONCE
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config

    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    if (originalRequest) {
      ensureCurrentAccount(originalRequest)
    }

    // Skip not found errors if specified in config
    if (error.response?.status === 404 && originalRequest?.skipNotFound) {
      return Promise.reject(error)
    }

    // Handle Problem Details format errors
    if (error.response?.status !== 401 && error.response?.data && isProblemDetailsResponse(error.response.data)) {
      const problemDetails = error.response.data
      showProblemDetailsToast(problemDetails)
      return Promise.reject(createProblemDetailsError(error))
    }

    // Handle 401 — retry with fresh token once
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !(originalRequest as ExtendedAxiosRequestConfig)._retry
    ) {
      ;(originalRequest as ExtendedAxiosRequestConfig)._retry = true

      const account = originalRequest.authAccount ?? msalInstance.getActiveAccount()
      if (account) {
        let accessToken = ''
        try {
          accessToken = await acquireAccessToken(account, true)
          ensureCurrentAccount(originalRequest)
        } catch (tokenError) {
          if (axios.isCancel(tokenError)) {
            return Promise.reject(tokenError)
          }

          console.error('Silent token acquisition failed:', tokenError)
          msalInstance.logoutRedirect()
        }

        if (accessToken) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          return apiClient(originalRequest)
        } else {
          msalInstance.loginRedirect()
        }
      }
    }

    // Fallback error toasts for non-ProblemDetails responses
    if (error.response) {
      switch (error.response.status) {
        case 422:
          showToast('Something is wrong with the inserted data', 'error')
          break
        case 500:
          showToast('Something went wrong. Try again later', 'error')
          break
        case 403:
          showToast("You don't have permission to perform this action", 'error')
          break
        case 404:
          showToast('The requested resource was not found', 'error')
          break
        default:
          showToast('An unexpected error occurred', 'error')
          break
      }
    } else if (error.request) {
      showToast('Network error. Please check your connection', 'error')
    } else {
      showToast('An unexpected error occurred', 'error')
    }

    return Promise.reject(error)
  },
)

export { apiClient }
